import { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  AreaChart as RechartsAreaChart,
  XAxis,
} from "recharts";

import { ChartLegend, ChartLegendContent } from "@/components/ui/ChartLegend";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  chartColorCssVarKey,
} from "@/components/ui/ChartTooltip";
import {
  CHART_VALUE_AXIS_TOP_MARGIN_PX,
  ColumnChartValueYAxis,
  useColumnChartValueAxis,
} from "@/components/ui/columnChartValueAxis";
import { useReportTheme } from "@/components/ui/report/ReportThemeProvider";
import { cn } from "@/lib/utils";
import { formatCategoryAxisTick } from "@/lib/utils/chartAxisTickFormatter";
import { resolveChartShowLegend } from "@/lib/utils/resolveChartShowLegend";

export type AreaDatum = Record<string, string | number | undefined>;

type AreaSeriesProps =
  | {
      dataKey: string;
      dataKeys?: never;
    }
  | {
      dataKey?: never;
      dataKeys: { key: string; label: string }[];
    };

export type AreaChartViewProps = {
  data: AreaDatum[];
  chartConfig: ChartConfig;
  xAxisKey: string;
  title?: string | null;
  description?: string | null;
  height?: number | null;
  className?: string;
  /** When set to a positive number, category tick labels are truncated to this length; omit for full labels. */
  xAxisTickChars?: number;
  fillOpacity?: number;
  gradient?: boolean;
  isAnimationActive?: boolean;
  accessibilityLayer?: boolean;
  showLegend?: boolean;
} & AreaSeriesProps;

const AreaChartEmpty = () => (
  <div className="flex w-full min-w-0 items-center justify-center rounded-md border border-dashed border-muted-foreground/25 py-4 text-xxs text-muted-foreground">
    No data to display
  </div>
);

const AreaChartView = ({
  data,
  chartConfig,
  xAxisKey,
  dataKey,
  dataKeys,
  title = null,
  description = null,
  height = null,
  className,
  xAxisTickChars,
  fillOpacity = 0.4,
  gradient = true,
  isAnimationActive = true,
  accessibilityLayer = true,
  showLegend: showLegendProp,
}: AreaChartViewProps) => {
  const inReportContext = useReportTheme() != null;
  const seriesList =
    dataKeys != null
      ? dataKeys
      : dataKey != null
        ? [
            {
              key: dataKey,
              label: String(chartConfig[dataKey]?.label ?? dataKey),
            },
          ]
        : [];

  const hasValues =
    data.length > 0 &&
    seriesList.length > 0 &&
    data.some((row) =>
      seriesList.some((series) => {
        const v = row[series.key];
        const n = typeof v === "number" ? v : Number(v);
        return !Number.isNaN(n);
      }),
    );

  const valueKeys = useMemo(() => {
    if (dataKeys != null) return dataKeys.map((series) => series.key);
    if (dataKey != null) return [dataKey];
    return [];
  }, [dataKeys, dataKey]);

  const valueAxis = useColumnChartValueAxis(data, { valueKeys });

  const showLegend = resolveChartShowLegend(
    showLegendProp,
    seriesList.length,
    inReportContext,
    false,
  );

  if (!hasValues) {
    return (
      <div className="flex w-full min-w-0 flex-col gap-1.5">
        {title != null && title !== "" ? (
          <p className="m-0 text-xs font-medium text-foreground">{title}</p>
        ) : null}
        <AreaChartEmpty />
      </div>
    );
  }

  return (
    <div
      className="flex w-full min-w-0 flex-col gap-1.5"
      style={height != null ? { minHeight: height, height } : undefined}
    >
      {title != null && title !== "" ? (
        <p className="m-0 text-xs font-medium text-foreground">{title}</p>
      ) : null}
      {description != null && description !== "" ? (
        <p className="m-0 text-xs text-muted-foreground">{description}</p>
      ) : null}
      <ChartContainer
        config={chartConfig}
        className={cn(
          "mx-auto aspect-video w-full justify-center",
          height == null ? "max-h-[240px]" : "max-h-[min(280px,100%)]",
          className,
        )}
      >
        <RechartsAreaChart
          accessibilityLayer={accessibilityLayer}
          data={data}
          margin={{
            top: CHART_VALUE_AXIS_TOP_MARGIN_PX,
            left: 12,
            right: 12,
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey={xAxisKey}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={formatCategoryAxisTick(xAxisTickChars)}
          />
          <ColumnChartValueYAxis
            calculatedYAxisWidth={valueAxis.calculatedYAxisWidth}
            yAxisDomain={valueAxis.yAxisDomain}
            onWidthChange={valueAxis.handleYAxisLabelWidthChange}
          />
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent indicator="line" />}
          />
          {gradient ? (
            <defs>
              {seriesList.map((series) => {
                const cssVarKey = chartColorCssVarKey(series.key);
                const stopColor = `var(--color-${cssVarKey})`;
                return (
                  <linearGradient
                    key={series.key}
                    id={`area-gradient-${cssVarKey}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor={stopColor} stopOpacity={0.5} />
                    <stop
                      offset="95%"
                      stopColor={stopColor}
                      stopOpacity={0.08}
                    />
                  </linearGradient>
                );
              })}
            </defs>
          ) : null}
          {seriesList.map((series) => {
            const cssVarKey = chartColorCssVarKey(series.key);
            const seriesColorVar = `var(--color-${cssVarKey})`;
            return (
              <Area
                key={series.key}
                dataKey={series.key}
                name={series.label}
                type="monotone"
                fill={
                  gradient ? `url(#area-gradient-${cssVarKey})` : seriesColorVar
                }
                fillOpacity={gradient ? 1 : Math.min(fillOpacity, 0.25)}
                stroke={seriesColorVar}
                isAnimationActive={isAnimationActive}
              />
            );
          })}
          {showLegend ? (
            <ChartLegend
              verticalAlign="bottom"
              content={
                <ChartLegendContent className="flex-wrap gap-x-2 gap-y-0.5 pt-1.5 text-foreground dark:text-gray-300" />
              }
            />
          ) : null}
        </RechartsAreaChart>
      </ChartContainer>
    </div>
  );
};

export default AreaChartView;
